/**
 * Enable-Banking mutation hooks (Phase 2, §7.5).
 *
 * Every mutation goes through the P1 `api` wrapper, which attaches the CSRF
 * header `X-Requested-With: fetch` to all non-GET requests. Invalidation map
 * (§7.5):
 *   saveCredential   → ['provider-credentials']
 *   deleteCredential → ['provider-credentials'] + ['connections']
 *   authorize        → (no cache write — the caller redirects to the bank)
 *   syncConnection   → ['connections'] + ['sync','runs'] + ['accounts'] +
 *                      ['transactions'] + ['inbox']
 *   revokeConnection → ['connections'] + ['accounts']
 *   linkAccount      → ['connections'] + ['accounts'] + ['transactions']
 *
 * The private key is submitted once here (POST body) and is never read back or
 * placed in a query string.
 *
 * `syncConnection` is proxy-timeout resilient: an attended sync outliving the
 * reverse proxy's read timeout surfaces here as a gateway-shaped error (504/502
 * HTML page or a dropped socket) while the run KEEPS RUNNING server-side. The
 * mutation then falls back to polling `GET /api/sync/runs` for the run's real
 * outcome instead of reporting a false failure.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api, ApiRequestError } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  AccountLinkIn,
  AccountOut,
  ConnectAuthorizeIn,
  ConnectAuthorizeOut,
  ProviderCredentialCreate,
  ProviderCredentialOut,
  SyncRunOut,
  SyncTrigger,
  SyncTriggerIn,
} from '../types.ts';

/** Invalidate `['sync','runs',*]` regardless of params. */
const SYNC_RUNS_PREFIX = ['sync', 'runs'] as const;

/** POST /api/provider-credentials — store/upsert the EB app credential. */
export function useSaveCredential(): UseMutationResult<
  ProviderCredentialOut,
  Error,
  ProviderCredentialCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProviderCredentialCreate) =>
      api.post<ProviderCredentialOut>('/api/provider-credentials', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.providerCredential });
    },
  });
}

/**
 * POST /api/connections/eb/authorize — start (or re-auth) a bank connection.
 * Returns the SCA `authorization_url`; the caller redirects the browser there.
 * No cache invalidation — nothing changed until the user returns via callback.
 */
export function useAuthorizeConnection(): UseMutationResult<
  ConnectAuthorizeOut,
  Error,
  ConnectAuthorizeIn
> {
  return useMutation({
    mutationFn: (body: ConnectAuthorizeIn) =>
      api.post<ConnectAuthorizeOut>('/api/connections/eb/authorize', body),
  });
}

export interface SyncConnectionVars {
  id: number;
  body?: SyncTriggerIn;
}

/** Recovery polling knobs — exported so tests can shrink the waits. */
export const SYNC_RECOVERY_TIMING = {
  pollMs: 5_000,
  deadlineMs: 5 * 60_000,
};

/**
 * A gateway-shaped failure: the reverse proxy answered with its own 5xx page
 * (`invalid_response` — the body wasn't the API's JSON envelope) or the socket
 * died (`network_error`). The attended run usually keeps running server-side,
 * so these are recovery candidates — unlike the API's structured errors
 * (409 `consent_required`, 429 `rate_limited`, …), which are definitive.
 */
function isGatewayShaped(err: unknown): boolean {
  return (
    err instanceof ApiRequestError &&
    (err.code === 'network_error' || (err.code === 'invalid_response' && err.status >= 500))
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function latestRunId(connectionId: number): Promise<number> {
  const runs = await api.get<SyncRunOut[]>(
    `/api/sync/runs?connection_id=${connectionId}&limit=1`,
  );
  return runs[0]?.id ?? 0;
}

/**
 * Poll `GET /api/sync/runs` until the attended run this mutation triggered
 * (`id > baselineId` — an older run can never be mistaken for it) reaches a
 * terminal status; `null` when the deadline passes without one.
 */
async function recoverRun(
  connectionId: number,
  baselineId: number,
  trigger: SyncTrigger,
): Promise<SyncRunOut | null> {
  const deadline = Date.now() + SYNC_RECOVERY_TIMING.deadlineMs;
  while (Date.now() < deadline) {
    await sleep(SYNC_RECOVERY_TIMING.pollMs);
    let runs: SyncRunOut[];
    try {
      runs = await api.get<SyncRunOut[]>(
        `/api/sync/runs?connection_id=${connectionId}&limit=10`,
      );
    } catch {
      continue; // proxy/network still unhappy — keep waiting
    }
    const run = runs.find((r) => r.id > baselineId && r.trigger === trigger && r.attended);
    if (run !== undefined && run.status !== 'running') return run;
  }
  return null;
}

/** Mirror the sync endpoint's own error mapping for an error-status run. */
function runToError(run: SyncRunOut): ApiRequestError {
  if (run.accounts.some((a) => a.error === 'rate_limited')) {
    return new ApiRequestError(
      'Zu viele Anfragen an die Bank. Bitte später erneut versuchen.',
      429,
      'rate_limited',
    );
  }
  return new ApiRequestError('Der Bank-Dienst ist derzeit nicht erreichbar.', 502, 'eb_upstream_error');
}

/**
 * POST /api/connections/{id}/sync — attended manual sync or bootstrap. Touches
 * the ledger, balances and the inbox, so it invalidates the full downstream set.
 * On a gateway-shaped failure the run's outcome is recovered from
 * `GET /api/sync/runs` (see module docstring).
 */
export function useSyncConnection(): UseMutationResult<SyncRunOut, Error, SyncConnectionVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: SyncConnectionVars) => {
      const payload: SyncTriggerIn = body ?? { trigger: 'manual' };
      const trigger: SyncTrigger = payload.trigger ?? 'manual';
      // Baseline BEFORE the trigger, so recovery only trusts newer runs. If
      // this read already fails, the network is gone for real — skip recovery
      // and let the POST produce the definitive error.
      let baselineId: number | null;
      try {
        baselineId = await latestRunId(id);
      } catch {
        baselineId = null;
      }
      try {
        return await api.post<SyncRunOut>(`/api/connections/${id}/sync`, payload);
      } catch (err) {
        if (baselineId === null || !isGatewayShaped(err)) throw err;
        const run = await recoverRun(id, baselineId, trigger);
        if (run === null) throw err;
        if (run.status === 'error') throw runToError(run);
        return run;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: SYNC_RUNS_PREFIX });
      void qc.invalidateQueries({ queryKey: qk.accounts });
      void qc.invalidateQueries({ queryKey: qk.transactions });
      void qc.invalidateQueries({ queryKey: qk.inbox });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}

/** DELETE /api/connections/{id} — best-effort revoke; detaches accounts. */
export function useRevokeConnection(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/connections/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: qk.accounts });
    },
  });
}

export interface LinkAccountVars {
  /** The discovered account row id (app `accounts.id`). */
  accountId: number;
  body: AccountLinkIn;
}

/**
 * POST /api/accounts/{id}/link — confirm auto-mapping (`merge_into_account_id`
 * null) or merge the discovered account into an existing manual account.
 */
export function useLinkAccount(): UseMutationResult<AccountOut, Error, LinkAccountVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }: LinkAccountVars) =>
      api.post<AccountOut>(`/api/accounts/${accountId}/link`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.connections });
      void qc.invalidateQueries({ queryKey: qk.accounts });
      void qc.invalidateQueries({ queryKey: qk.transactions });
    },
  });
}
